import React, { useState, useRef, useCallback, useEffect } from 'react';
import caret from './packages/components/assets/images/caret.svg.js';

const TEXT_TYPE_TIMEOUT = 2000;
const DropdownOptions = ({ listRef, isOpen, options, highlightedIndex, activeOption, setHighlightedIndex, handleOptionSelect, }) => {
    if (!isOpen)
        return null;
    return (React.createElement("ul", { ref: listRef, role: "listbox", className: "rounded-lg w-full bg-white absolute m-0 mt-1 p-0 z-10 list-none max-h-80 border border-gray-300 overflow-auto" }, options.map((option, index) => (React.createElement("li", { key: option.value, id: `option-${option.value}`, role: "option", className: [
            'cursor-pointer py-2 px-3 text-sm',
            activeOption?.value === option.value ? '!bg-lilac-300' : '',
            highlightedIndex === index ? 'bg-green-100' : 'bg-transparent',
        ].join(' '), "aria-selected": highlightedIndex === index, onClick: e => handleOptionSelect(option, index), onMouseMove: () => setHighlightedIndex(index) }, option.label)))));
};
const SelectFieldMinimal = ({ icon, options, error, labelClassName, disabled, placeholder = 'Select State', onSelect, defaultValue, }) => {
    const [isLoaded, setIsLoaded] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [activeOption, setActiveOption] = useState(defaultValue);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const [typedText, setTypedText] = useState('');
    const selectRef = useRef(null);
    const listRef = useRef(null);
    const typedTextTimeout = useRef(null);
    const handleDocumentClick = useCallback((event) => {
        if (selectRef.current &&
            !selectRef.current.contains(event.target)) {
            setIsOpen(false);
        }
    }, []);
    useEffect(() => {
        setIsLoaded(true);
    }, []);
    useEffect(() => {
        document.addEventListener('click', handleDocumentClick);
        return () => {
            document.removeEventListener('click', handleDocumentClick);
        };
    }, [handleDocumentClick]);
    useEffect(() => {
        if (typedTextTimeout.current)
            clearTimeout(typedTextTimeout.current);
        typedTextTimeout.current = setTimeout(() => setTypedText(''), TEXT_TYPE_TIMEOUT);
    }, [typedText]);
    useEffect(() => {
        return () => {
            if (typedTextTimeout.current)
                clearTimeout(typedTextTimeout.current);
        };
    }, []);
    useEffect(() => {
        if (defaultValue) {
            setActiveOption(defaultValue);
            setHighlightedIndex(options.findIndex(option => option.value === defaultValue.value));
        }
    }, [defaultValue, options]);
    const scrollToElement = useCallback((index) => {
        const element = document.getElementById(`option-${options[index].value}`);
        if (element) {
            element.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        }
    }, []);
    useEffect(() => {
        if (isOpen) {
            const matchedIndex = options.findIndex(option => option.value === activeOption?.value);
            if (matchedIndex !== -1) {
                setHighlightedIndex(matchedIndex);
                scrollToElement(matchedIndex);
            }
        }
    }, [isOpen]);
    useEffect(() => {
        if (typedText) {
            const matchedIndex = options.findIndex(option => option.label.toLowerCase().startsWith(typedText.toLowerCase()));
            if (matchedIndex !== -1) {
                setHighlightedIndex(matchedIndex);
                scrollToElement(matchedIndex);
            }
        }
    }, [typedText, options, scrollToElement]);
    useEffect(() => {
        setTypedText('');
    }, [isOpen]);
    const handleOptionSelect = (option, index) => {
        setActiveOption(option);
        setIsOpen(false);
        onSelect(option);
        setHighlightedIndex(index);
        setTypedText('');
    };
    const handleKeyDown = (event) => {
        event.preventDefault();
        if (event.key === 'ArrowDown') {
            setHighlightedIndex(prevIndex => {
                const newIndex = (prevIndex + 1) % options.length;
                scrollToElement(newIndex);
                return (prevIndex + 1) % options.length;
            });
        }
        else if (event.key === 'ArrowUp') {
            setHighlightedIndex(prevIndex => {
                const newIndex = (prevIndex + options.length - 1) % options.length;
                scrollToElement(newIndex);
                return (prevIndex + options.length - 1) % options.length;
            });
        }
        else if (event.key === 'Enter' && highlightedIndex >= 0) {
            handleOptionSelect(options[highlightedIndex], highlightedIndex);
        }
        else if (event.key.length === 1) {
            setTypedText(prevText => prevText + event.key);
        }
        else if (event.key === 'Backspace') {
            setTypedText(prevText => prevText.slice(0, -1));
        }
    };
    return (React.createElement("div", { ref: selectRef, className: "relative inline-block" },
        React.createElement("button", { type: "button", className: [
                'bg-white flex items-center gap-2 placeholder-gray-600 appearance-none border rounded-lg outline-none focus:outline-none transition-colors border-gray-300 secondary:border-indigo-200 focus:border-lilac-500 focus:secondary:border-lilac-500 w-full p-3 pr-3.5 bg-no-repeat bg-input-position text-left bg-select bg-input-size overflow-ellipsis whitespace-nowrap',
                disabled ? 'cursor-no-drop opacity-75' : '',
                error ? 'border-red-500' : '',
            ].join(' '), disabled: disabled, "aria-haspopup": "listbox", "aria-expanded": isOpen, onClick: () => setIsOpen(!isOpen), onKeyDown: handleKeyDown },
            icon && (React.createElement("img", { className: "h-auto my-reset w-3 object-contain", src: icon, alt: "icon" })),
            React.createElement("span", { className: [
                    'text-ellipsis overflow-hidden truncate',
                    labelClassName,
                ].join(' ') }, isLoaded && activeOption ? activeOption?.label : placeholder),
            React.createElement("img", { className: "ml-auto scale-125", src: caret, alt: "" })),
        React.createElement(DropdownOptions, { isOpen: isOpen, activeOption: activeOption, highlightedIndex: highlightedIndex, listRef: listRef, options: options, setHighlightedIndex: setHighlightedIndex, handleOptionSelect: handleOptionSelect })));
};

export { SelectFieldMinimal };
//# sourceMappingURL=SelectFieldMinimal.js.map
